// useSipEngine — sip.js wrapper for WebRTC softphone (Phase 3).
//
// Loads SIP creds from `GET /api/me/sip-credentials`, REGISTERs against the
// Asterisk WSS endpoint, then exposes invite/answer/terminate/dtmf/mute/hold
// + a live `state` machine the Softphone widget can render.
//
// Only activates when telephony_mode === "live". In simulator mode the hook
// returns `mode: "simulator"` + no-op actions so the existing widget keeps
// working as-is.
import { useEffect, useRef, useState, useCallback } from "react";
import {
  UserAgent, Registerer, Inviter, SessionState, RegistererState, TransportState,
} from "sip.js";
import api from "@/lib/api";

const SESSION_STATE_MAP = {
  [SessionState.Initial]: "initial",
  [SessionState.Establishing]: "ringing",
  [SessionState.Established]: "talking",
  [SessionState.Terminating]: "ending",
  [SessionState.Terminated]: "ended",
};

export function useSipEngine({ enabled = true } = {}) {
  const [creds, setCreds] = useState(null);          // /me/sip-credentials payload
  const [mode, setMode] = useState("loading");        // "live" | "simulator" | "error" | "loading"
  const [registered, setRegistered] = useState(false);
  const [transportConnected, setTransportConnected] = useState(false);
  const [registerError, setRegisterError] = useState("");
  const [credError, setCredError] = useState("");    // 403/404/409 reason (no license/extension)
  const [session, setSession] = useState(null);       // { id, direction, peer, state, muted, held }
  const [incomingInvitation, setIncomingInvitation] = useState(null);
  // Synchronous mirror of `incomingInvitation` so onInvite / Terminated can
  // compare identities WITHOUT waiting for a React state flush (the source of
  // the "popup vanished but it's still ringing" race: a late Terminated from
  // an already-cancelled INVITE would null out a NEWER live invitation).
  const incomingInvitationRef = useRef(null);
  const [monitorActive, setMonitorActive] = useState(false);  // silent supervisor monitor leg
  const monitorRef = useRef(null);
  const uaRef = useRef(null);
  const registererRef = useRef(null);
  const sessionRef = useRef(null);
  const audioElRef = useRef(null);
  const ringbackRef = useRef(null);   // Web-Audio ringback generator

  // Lazy-load the audio sink element (one global hidden <audio>)
  useEffect(() => {
    if (audioElRef.current) return;
    const el = document.createElement("audio");
    el.autoplay = true;
    el.setAttribute("data-testid", "sip-remote-audio");
    el.style.display = "none";
    document.body.appendChild(el);
    audioElRef.current = el;
    return () => { try { document.body.removeChild(el); } catch { /* ignore */ } };
  }, []);

  // -------- Local ringback generator (440 Hz + 480 Hz, 2s on / 4s off) ------
  // sip.js receives `180 Ringing` from Asterisk but has no built-in tone;
  // traditional SIP phones generate ringback locally. We do the same here
  // via Web Audio API — runs purely client-side, no audio file needed.
  function startRingback() {
    try {
      if (ringbackRef.current) return;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const o1 = ctx.createOscillator(); o1.frequency.value = 440;
      const o2 = ctx.createOscillator(); o2.frequency.value = 480;
      const gain = ctx.createGain(); gain.gain.value = 0;
      o1.connect(gain); o2.connect(gain); gain.connect(ctx.destination);
      o1.start(); o2.start();
      // Cadence: 2 s on, 4 s off — North American standard; sounds correct in India too
      const cadence = () => {
        const t = ctx.currentTime;
        gain.gain.setValueAtTime(0.05, t);
        gain.gain.setValueAtTime(0,    t + 2);
      };
      cadence();
      const iv = setInterval(cadence, 6000);
      ringbackRef.current = { ctx, iv };
    } catch { /* ignore */ }
  }
  function stopRingback() {
    const rb = ringbackRef.current;
    if (!rb) return;
    try { clearInterval(rb.iv); rb.ctx.close(); } catch { /* ignore */ }
    ringbackRef.current = null;
  }

  // -------- Incoming ringtone (distinct from outbound ringback) ------------
  // Plays while an inbound invitation is pending so the agent actually HEARS
  // the call ringing. Web-Audio warble (440/480 Hz, 1s on / 2s off), louder
  // than ringback so it's clearly audible over a headset.
  const ringtoneRef = useRef(null);
  function startRingtone() {
    try {
      if (ringtoneRef.current) return;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const o1 = ctx.createOscillator(); o1.frequency.value = 440;
      const o2 = ctx.createOscillator(); o2.frequency.value = 480;
      const gain = ctx.createGain(); gain.gain.value = 0;
      o1.connect(gain); o2.connect(gain); gain.connect(ctx.destination);
      o1.start(); o2.start();
      const cadence = () => {
        const t = ctx.currentTime;
        gain.gain.setValueAtTime(0.12, t);
        gain.gain.setValueAtTime(0,    t + 1);
      };
      cadence();
      const iv = setInterval(cadence, 3000);
      ringtoneRef.current = { ctx, iv };
    } catch { /* ignore */ }
  }
  function stopRingtone() {
    const rt = ringtoneRef.current;
    if (!rt) return;
    try { clearInterval(rt.iv); rt.ctx.close(); } catch { /* ignore */ }
    ringtoneRef.current = null;
  }

  // Single source of truth for the incoming ringtone: it plays iff there is a
  // pending un-answered incoming invitation, and stops the instant that
  // invitation is cleared (answered / rejected / cancelled by Asterisk on a
  // ring-timeout roll-over). Deriving it from state — instead of scattered
  // manual start/stop calls — is what prevents the ringtone from continuing
  // after the popup disappears (and vice-versa).
  useEffect(() => {
    if (incomingInvitation) startRingtone();
    else stopRingtone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingInvitation]);

  // Safety net: if the workspace unmounts / agent logs out while a call is
  // pending, kill any audio cadence so the ringtone can never outlive the UI.
  useEffect(() => () => { stopRingtone(); stopRingback(); }, []);

  // Fetch creds + bring up UA
  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    (async () => {
      try {
        const r = await api.get("/me/sip-credentials");
        if (stopped) return;
        setCreds(r.data);
        if ((r.data.telephony_mode || "").toLowerCase() !== "live") {
          setMode("simulator");
          return;
        }
        setMode("live");
        await startUa(r.data);
      } catch (e) {
        if (!stopped) {
          setMode("simulator");
          const status = e?.response?.status;
          // 403/404/409 = this user has no agent_license / extension yet — the
          // softphone can't come up. Surface the exact reason (instead of a
          // misleading "simulator" badge) so an admin can fix provisioning.
          if ([403, 404, 409].includes(status)) {
            setCredError(e?.response?.data?.detail || "No extension / license assigned for calling.");
          } else if (status) {
            setRegisterError(e?.response?.data?.detail || "Failed to load SIP credentials");
          }
        }
      }
    })();
    return () => {
      stopped = true;
      stopUa();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Re-REGISTER using the current registerer (created in startUa). Safe to call
  // repeatedly — used on first bring-up AND after a transport reconnect (e.g.
  // Asterisk restarted under us) so the agent's PJSIP contact is re-asserted.
  async function registerNow() {
    const registerer = registererRef.current;
    if (!registerer) return;
    try {
      await registerer.register({
        requestDelegate: {
          onReject: (rsp) =>
            setRegisterError(`REGISTER rejected: ${rsp.message.statusCode} ${rsp.message.reasonPhrase}`),
        },
      });
    } catch (e) {
      setRegisterError(e?.message || String(e));
    }
  }

  async function startUa(c) {
    try {
      const uri = UserAgent.makeURI(`sip:${c.username}@${c.sip_domain}`);
      const ua = new UserAgent({
        uri,
        transportOptions: { server: c.ws_url },
        // Built-in transport auto-reconnect: when Asterisk restarts (redeploy)
        // the WSS drops — SIP.js retries the transport for us. We ALSO listen
        // to transport.stateChange below to re-REGISTER once it's back (the UA
        // reconnects the socket but does NOT re-register on its own).
        reconnectionAttempts: 60,
        reconnectionDelay: 3,
        authorizationUsername: c.username,
        authorizationPassword: c.password,
        displayName: c.display_name || c.username,
        delegate: {
          onInvite: (invitation) => {
            // Inbound call
            const remote = invitation.remoteIdentity?.uri?.user || "unknown";
            const remoteName = invitation.remoteIdentity?.displayName || "";
            // Slice 15 — a supervisor MONITOR leg (Spy/Whisper/Barge) is
            // originated with callerId "Monitor". Auto-answer it SILENTLY so
            // the supervisor hears the call instantly — no ringtone, no popup,
            // and it never touches the agent's normal call session UI.
            if (remoteName === "Monitor" || remote.toLowerCase() === "monitor") {
              monitorRef.current = invitation;
              setMonitorActive(true);
              invitation.stateChange.addListener((state) => {
                if (state === SessionState.Terminated) {
                  monitorRef.current = null;
                  setMonitorActive(false);
                }
              });
              invitation
                .accept({ sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } } })
                .then(() => attachRemoteAudioSink(invitation))
                .catch(() => { monitorRef.current = null; setMonitorActive(false); });
              return;
            }
            // If an ESTABLISHED call is already up on this softphone, politely
            // reject the new INVITE — app_queue's `ringinuse=no` normally
            // prevents this, but never end the agent's live call for a race.
            const cur = sessionRef.current;
            if (cur && cur.state === SessionState.Established) {
              try { invitation.reject(); } catch { /* ignore */ }
              return;
            }
            // Drop any STALE pending incoming invitation (e.g. Asterisk is
            // re-ringing us after a ring-timeout roll-over: the previous
            // INVITE is about to be — or was just — cancelled). The newest
            // INVITE becomes the single active incoming call so the popup +
            // ringtone always track the live call, never a dead one.
            const prevInc = incomingInvitationRef.current;
            if (prevInc && prevInc !== invitation) {
              try { prevInc.reject?.(); } catch { /* ignore */ }
            }
            incomingInvitationRef.current = invitation;
            setIncomingInvitation(invitation);
            setSession({
              id: invitation.id,
              direction: "inbound",
              peer: remote,
              peer_name: invitation.remoteIdentity?.displayName || remote,
              state: "ringing",
              muted: false, held: false,
            });
            sessionRef.current = invitation;
            wireSessionEvents(invitation, "inbound");
          },
        },
        sessionDescriptionHandlerFactoryOptions: {
          // Cap ICE gathering — sip.js otherwise waits up to ~5 s for STUN
          // srflx candidates before sending the SDP answer on accept(),
          // which shows up as a 4–5 s delay between clicking Answer and the
          // call connecting. Host candidates gather instantly; 1 s is plenty.
          iceGatheringTimeout: 1000,
          peerConnectionConfiguration: {
            iceServers: (c.stun_servers || []).map((s) => ({ urls: s })),
          },
        },
      });
      uaRef.current = ua;
      await ua.start();
      // Transport lifecycle — surface connectivity + re-register on reconnect.
      const wasConnectedRef = { current: false };
      try {
        ua.transport.stateChange.addListener((state) => {
          if (state === TransportState.Connected) {
            setTransportConnected(true);
            // If this is a RE-connect (we'd been connected before and dropped),
            // the previous registration is dead on the new Asterisk — re-REGISTER.
            if (wasConnectedRef.current) {
              // eslint-disable-next-line no-console
              console.info("[sip-engine] transport reconnected → re-REGISTER");
              registerNow();
            }
            wasConnectedRef.current = true;
          } else if (state === TransportState.Disconnected) {
            setTransportConnected(false);
            setRegistered(false);
          }
        });
      } catch { /* older sip.js — transport listener unavailable */ }
      const registerer = new Registerer(ua, { expires: 600 });
      registererRef.current = registerer;
      registerer.stateChange.addListener((state) => {
        if (state === RegistererState.Registered) {
          setRegistered(true); setRegisterError("");
        } else if (state === RegistererState.Unregistered) {
          setRegistered(false);
        } else if (state === RegistererState.Terminated) {
          setRegistered(false);
        }
      });
      setTransportConnected(true);
      wasConnectedRef.current = true;
      await registerNow();
    } catch (e) {
      setMode("error");
      setRegisterError(e?.message || String(e));
    }
  }

  // Force a full SIP re-establish — reconnect the transport (if down) and
  // re-REGISTER. Called by the workspace recovery flow after an outage so the
  // agent's line is live again WITHOUT a logout/login. Returns true if the
  // transport is connected afterwards.
  const reconnect = useCallback(async () => {
    if (mode !== "live" || !uaRef.current) return false;
    const ua = uaRef.current;
    try {
      const st = ua.transport?.state;
      if (st !== TransportState.Connected) {
        await ua.reconnect();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[sip-engine] reconnect() failed:", e?.message || e);
    }
    await registerNow();
    return uaRef.current?.transport?.state === TransportState.Connected;
  }, [mode]);

  function wireSessionEvents(s, direction = "outbound") {
    // Attach remote audio sink the moment Asterisk starts sending RTP — that
    // happens during "early media" (e.g. Plivo's ringback tone before answer),
    // not just on Established. Hooking `peerConnection.ontrack` covers both.
    // The moment real carrier audio arrives we ALSO stop the synthetic
    // ringback fallback, so the agent hears the actual provider tone /
    // "switched off" / "busy" / IVR prompt instead of a generic tone.
    const stop = attachRemoteAudioSink(s, {
      onRealAudio: () => stopRingback(),
    });

    // Fallback synthetic ringback — fires 1.2 s after invite() if no real
    // carrier early media has arrived by then. Plays only for outbound
    // calls (inbound has its own incoming ring). Stopped automatically by
    // attachRemoteAudioSink() the instant real RTP starts.
    let synthRingbackTimer = null;
    if (direction === "outbound") {
      synthRingbackTimer = setTimeout(() => {
        if (s.state !== SessionState.Established) startRingback();
      }, 1200);
    }

    s.stateChange.addListener((state) => {
      const mapped = SESSION_STATE_MAP[state] || String(state);
      // Identity guard: only mutate the SHARED session/incoming state when
      // this listener's session is still the active one. A stale/late event
      // from a previous (cancelled) INVITE must NOT overwrite a newer live
      // call's UI (root cause of the popup flipping to "ended"/vanishing while
      // a fresh call is actually ringing).
      const isActive = sessionRef.current === s;
      const isActiveIncoming = incomingInvitationRef.current === s;
      if (isActive) {
        setSession((cur) => cur ? { ...cur, state: mapped } : cur);
      }
      if (state === SessionState.Established) {
        stopRingback();
      }
      if (state === SessionState.Terminated) {
        stopRingback();
        if (synthRingbackTimer) clearTimeout(synthRingbackTimer);
        try { stop(); } catch { /* ignore */ }  // this session's own audio sink
        // Clear the incoming ring only if THIS invitation is still pending —
        // otherwise a late cancel of an old leg would silence a live re-ring.
        if (isActiveIncoming) {
          incomingInvitationRef.current = null;
          setIncomingInvitation(null);
        }
        if (!isActive) return;  // stale leg: leave the live call untouched
        if (audioElRef.current) audioElRef.current.srcObject = null;
        try {
          window.__c7RemoteAudioStream = null;
          window.dispatchEvent(new CustomEvent("c7:remote-audio", { detail: { hasAudio: false } }));
        } catch { /* ignore */ }
        sessionRef.current = null;
        // If the agent is STILL parked off-hook, restore the ConfBridge audio
        // to the shared sink we just nulled — otherwise the parked leg goes
        // one-way and the next predictive/auto/power customer is inaudible to
        // the agent (bug: "no customer voice after a manual dial").
        rebindOffHookAudio();
        // NOTE: We intentionally do NOT fire `dialer:ended` here. The
        // Softphone widget's WrapUpDialog will fire it only AFTER the
        // agent picks a disposition + saves — so the workspace CRM card
        // stays visible while wrap-up is in progress (mandatory flow).
        // Brief "ended" badge — but ONLY clear the UI if no NEW call has
        // taken over in the meantime. On a ring-timeout roll-over Asterisk
        // cancels this INVITE and immediately re-INVITEs (a fresh invitation
        // sets sessionRef again); without this guard the old leg's delayed
        // timeout would null the live re-ring's session, leaving the ringtone
        // playing with no answerable popup (the ~25s "popup vanished" bug).
        const endedSession = s;
        setTimeout(() => {
          if (sessionRef.current === null || sessionRef.current === endedSession) {
            setSession(null);
          }
        }, 1500);
      }
    });
  }

  // Slice 3.5i (round 6) — attach the inbound MediaStream from a sip.js
  // session's PeerConnection to the global hidden <audio> element.
  //
  // THIS IS WHAT WAS MISSING ON THE OFF-HOOK / PREDICTIVE PATH:
  // - Manual dial: useSipEngine.invite() → wireSessionEvents() → audio sink attached ✓
  // - Predictive park: useSipEngine.inviteOffHook() → NO wiring → audio sink NEVER attached ✗
  //
  // The PeerConnection successfully received 102 RTP packets from Asterisk
  // (proven in user's RTP debug log), decoded them, but the resulting
  // MediaStream was never bound to <audio>.srcObject → speaker hears
  // nothing → agent reports "one-way audio" while everything WAS in fact
  // working at the network layer.
  //
  // Returns a `stop()` cleanup function that removes listeners + clears
  // the audio element.
  function attachRemoteAudioSink(s, { onRealAudio } = {}) {
    let realAudioStarted = false;
    const apply = () => {
      const pc = s.sessionDescriptionHandler?.peerConnection;
      if (!pc || !audioElRef.current) return;
      const stream = new MediaStream();
      pc.getReceivers().forEach((r) => {
        if (r.track && r.track.kind === "audio") stream.addTrack(r.track);
      });
      if (stream.getAudioTracks().length > 0) {
        audioElRef.current.srcObject = stream;
        audioElRef.current.play().catch(() => {});
        // Expose the EXACT stream the agent is hearing so the AI Copilot can
        // transcribe it directly (no fragile DOM/captureStream re-discovery).
        try {
          window.__c7RemoteAudioStream = stream;
          window.dispatchEvent(new CustomEvent("c7:remote-audio", { detail: { hasAudio: true } }));
        } catch { /* ignore */ }
        if (!realAudioStarted) {
          realAudioStarted = true;
          try { onRealAudio && onRealAudio(); } catch { /* ignore */ }
        }
      }
    };
    let pc = null;
    const wireOnce = () => {
      pc = s.sessionDescriptionHandler?.peerConnection;
      if (!pc) return false;
      pc.addEventListener("track", apply);
      apply();
      return true;
    };
    let attempts = 0;
    const poll = setInterval(() => {
      if (wireOnce() || ++attempts > 80) clearInterval(poll);
    }, 50);
    return () => {
      clearInterval(poll);
      try { pc?.removeEventListener("track", apply); } catch { /* ignore */ }
    };
  }

  // Re-bind the off-hook park leg's ConfBridge audio to the shared <audio>
  // sink. Called when a FOREGROUND call (manual dial / inbound) ends while the
  // agent is STILL parked off-hook. The foreground call's Terminated handler
  // nulls `audioElRef.srcObject`; without re-binding, the parked leg goes
  // one-way — the agent keeps sending mic to the conf (customer hears them)
  // but their speaker stays bound to null, so the NEXT predictive/auto/power
  // customer's audio never plays (the "no customer voice after a manual dial"
  // bug). The park-leg PeerConnection is still Established and still receiving
  // the mixed conf RTP, so we just re-point the sink at it.
  function rebindOffHookAudio() {
    const s = offHookRef.current;
    const el = audioElRef.current;
    if (!s || !el) return;
    const pc = s.sessionDescriptionHandler?.peerConnection;
    if (!pc) return;
    const stream = new MediaStream();
    pc.getReceivers().forEach((r) => {
      if (r.track && r.track.kind === "audio") stream.addTrack(r.track);
    });
    if (stream.getAudioTracks().length > 0) {
      el.srcObject = stream;
      el.play().catch(() => {});
      try {
        window.__c7RemoteAudioStream = stream;
        window.dispatchEvent(new CustomEvent("c7:remote-audio", { detail: { hasAudio: true } }));
      } catch { /* ignore */ }
    }
  }

  function stopUa() {
    try { registererRef.current?.unregister(); } catch { /* ignore */ }
    try { uaRef.current?.stop(); } catch { /* ignore */ }
    uaRef.current = null;
    registererRef.current = null;
    sessionRef.current = null;
    incomingInvitationRef.current = null;
    setIncomingInvitation(null);
    stopRingtone();
  }

  // ----- Actions -----
  const invite = useCallback(async (destination) => {
    if (mode !== "live" || !uaRef.current || !creds) {
      throw new Error("SIP engine not in live mode");
    }
    if (sessionRef.current) throw new Error("Already on a call");
    const target = UserAgent.makeURI(`sip:${destination}@${creds.sip_domain}`);
    if (!target) throw new Error(`Invalid SIP target: ${destination}`);
    const inviter = new Inviter(uaRef.current, target, {
      // earlyMedia=true makes sip.js open the media stream on 183 Session
      // Progress (carrier ringback / "switched off" / "busy" / IVR prompts)
      // instead of waiting for 200 OK. Without it the agent hears silence
      // until the call is answered.
      earlyMedia: true,
      sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
    });
    sessionRef.current = inviter;
    setSession({
      id: inviter.id, direction: "outbound", peer: destination,
      peer_name: destination, state: "dialing", muted: false, held: false,
    });
    wireSessionEvents(inviter, "outbound");
    try {
      await inviter.invite();
    } catch (e) {
      sessionRef.current = null;
      setSession(null);
      throw e;
    }
  }, [mode, creds]);

  const answer = useCallback(async () => {
    const inv = incomingInvitationRef.current || incomingInvitation;
    if (!inv) return;
    // Clear the incoming ref BEFORE accept so the ringtone effect stops
    // immediately and no late cancel of a sibling leg can re-null our session.
    incomingInvitationRef.current = null;
    await inv.accept({
      sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
    });
    // CRITICAL (one-way audio fix): for an INCOMING call the WebRTC
    // peerConnection only exists AFTER accept(). The onInvite-time sink poll
    // has usually expired by now, so re-attach here where the PC is live —
    // otherwise the customer's RTP is received but never bound to <audio>
    // and the agent hears nothing (classic inbound one-way audio).
    attachRemoteAudioSink(inv, { onRealAudio: () => stopRingback() });
    setIncomingInvitation(null);
  }, [incomingInvitation]);

  const reject = useCallback(async () => {
    const inv = incomingInvitationRef.current || incomingInvitation;
    if (!inv) return;
    incomingInvitationRef.current = null;
    try { await inv.reject(); } catch { /* ignore */ }
    setIncomingInvitation(null);
  }, [incomingInvitation]);

  const terminate = useCallback(async () => {
    const s = sessionRef.current;
    if (!s) return;
    try {
      if (s.state === SessionState.Initial || s.state === SessionState.Establishing) {
        // Outgoing not yet answered — cancel; or incoming not yet accepted — reject
        if (s.cancel) await s.cancel();
        else if (s.reject) await s.reject();
      } else if (s.state === SessionState.Established) {
        await s.bye();
      } else {
        await s.dispose?.();
      }
    } catch { /* ignore */ }
  }, []);

  const sendDtmf = useCallback(async (digit) => {
    const s = sessionRef.current;
    if (!s || s.state !== SessionState.Established) return;
    try {
      // RFC 4733 via session.info, or sip.js helper
      const sdh = s.sessionDescriptionHandler;
      if (sdh && typeof sdh.sendDtmf === "function") {
        sdh.sendDtmf(digit);
      }
    } catch { /* ignore */ }
  }, []);

  const setMuted = useCallback((muted) => {
    const s = sessionRef.current;
    if (!s) return;
    const pc = s.sessionDescriptionHandler?.peerConnection;
    if (!pc) return;
    pc.getSenders().forEach((sender) => {
      if (sender.track && sender.track.kind === "audio") sender.track.enabled = !muted;
    });
    setSession((cur) => cur ? { ...cur, muted } : cur);
  }, []);

  const setHeld = useCallback(async (held) => {
    const s = sessionRef.current;
    if (!s || s.state !== SessionState.Established) return;
    try {
      await s.invite({
        sessionDescriptionHandlerOptions: {
          hold: held,
          constraints: { audio: true, video: false },
        },
        requestOptions: { extraHeaders: [] },
      });
      setSession((cur) => cur ? { ...cur, held } : cur);
    } catch (e) {
      // Some sip.js versions use s.refer / s.hold instead — silently ignore.
    }
  }, []);

  // Slice 3.5 — Off-hook park leg. Maintained as a SEPARATE session ref
  // from the regular call ref so an INVITE into the conf bridge doesn't
  // interfere with the agent's ability to see/answer ad-hoc calls UI-wise.
  // The leg lives quietly in the background; customer audio arrives mixed
  // by the Asterisk ConfBridge.
  const offHookRef = useRef(null);
  const [offHookActive, setOffHookActive] = useState(false);

  // Slice 3.5l — cool-down between park attempts. If a ConfBridge join
  // fails (e.g. Asterisk dialplan returns non-zero because
  // confbridge.conf bridge_profile is missing), our state-driven
  // useEffect in Softphone.jsx would otherwise re-fire INVITE
  // immediately, looping on the failure (visible in CLI as back-to-back
  // `Spawn extension exited non-zero` events for the agent extension).
  // 3 s backoff turns the loop into a slow retry the operator can see.
  const lastOffHookAttemptRef = useRef(0);
  const inviteOffHook = useCallback(async (dialTarget) => {
    if (mode !== "live" || !uaRef.current || !creds) return;
    if (offHookRef.current) return;  // already parked
    const now = Date.now();
    if (now - lastOffHookAttemptRef.current < 3000) {
      // eslint-disable-next-line no-console
      console.info("[sip-engine] off-hook retry suppressed (3s cooldown)");
      return;
    }
    lastOffHookAttemptRef.current = now;
    const target = UserAgent.makeURI(`sip:${dialTarget}@${creds.sip_domain}`);
    if (!target) return;
    const inv = new Inviter(uaRef.current, target, {
      earlyMedia: true,
      sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
    });
    offHookRef.current = inv;
    const stopSink = attachRemoteAudioSink(inv);
    inv.stateChange.addListener((state) => {
      if (state === SessionState.Terminated) {
        try { stopSink(); } catch { /* ignore */ }
        if (audioElRef.current) audioElRef.current.srcObject = null;
        try {
          window.__c7RemoteAudioStream = null;
          window.dispatchEvent(new CustomEvent("c7:remote-audio", { detail: { hasAudio: false } }));
        } catch { /* ignore */ }
        offHookRef.current = null;
        setOffHookActive(false);
      }
    });
    try {
      await inv.invite();
      setOffHookActive(true);
    } catch {
      try { stopSink(); } catch { /* ignore */ }
      offHookRef.current = null;
      setOffHookActive(false);
    }
  }, [mode, creds]);

  const terminateOffHook = useCallback(async () => {
    const s = offHookRef.current;
    if (!s) return;
    try {
      if (s.state === SessionState.Established) await s.bye();
      else if (typeof s.cancel === "function") await s.cancel();
    } catch { /* ignore */ }
    offHookRef.current = null;
    setOffHookActive(false);
  }, []);

  return {
    mode, creds, registered, registerError, credError, transportConnected,
    session, incomingInvitation, monitorActive,
    invite, answer, reject, terminate, reconnect,
    sendDtmf, setMuted, setHeld,
    // Slice 3.5
    offHookActive, inviteOffHook, terminateOffHook,
  };
}
